import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { logEvent } from '../../../../../new-lib/analytics';
import CoverSection from './components/CoverSection';
import { EntityTypes } from '../../../../../new-lib/constants/common';
import EntityDetails from '../../../components/entity-details/EntityDetails';
import ExploreProperties from '../../../components/explore-properties/ExplorePropertiesContainer';
import { SearchFiltersTypes } from '../../../../../reducers/search-filters/reducer';
import RequestMeetingBtn from '../../../../contact/components/request-meeting/RequestMeetingBtn';
import QuickView from '../../../../../new-components/display-system/QuickView';
import RequestMeetingContainer from '../../../../contact/components/request-meeting/RequestMeetingContainer';
import FormHeader from '../../../../contact/components/organic-form/FormHeader';
import SuggestedContainer from '../../../../../new-components/SuggestedContainer';
import StickyDetails from '../../../components/entity-details/StickyDetails';
import SoldOutSection from '../../../components/SoldOutSection';
import Description from '../../../components/Description';
import Amenities from '../../../components/Amenities';
import { useTranslation } from 'next-i18next';
import { FAQ } from '../../../../../new-lib/types/common';
import FAQs from '../../../components/FAQs';
import usePolygonType from '../../../../map/hooks/PolygonTypes';
import dynamic from 'next/dynamic';

const ContactFormContainer = dynamic(
  () =>
    import('../../../../contact/components/organic-form/ContactFormContainer'),
  { ssr: false }
);

interface LaunchViewProps {
  launch: any;
  hasAvailability: boolean;
  filterOptions: [];
  recommendedCompounds: [];
  breadcrumbs: React.ReactElement;
  faqs: FAQ[];
}

interface ContainerProps {
  locale?: string;
  webview?: boolean;
}

const LaunchView = ({
  launch,
  hasAvailability,
  filterOptions,
  recommendedCompounds,
  breadcrumbs,
  faqs
}: LaunchViewProps) => {
  const eventId = useSelector((state: any) => state.app.eventId);
  const [meetingForm, setMeetingForm] = useState<boolean>(false);
  const webview = useSelector((state: any) => state.app.webview);
  const { t } = useTranslation('common');
  const { getPolygonType } = usePolygonType();
  const handleRequestMeetingClick = () => {
    logEvent.contact('form_physical_meeting', `CompoundPage`, eventId, {
      entity_type: EntityTypes.COMPOUND,
      cmpnd_id: launch.id,
      cmpnd_name: launch.name,
      org_id: launch.nawy_organization_id ?? 1
    });
    setMeetingForm(true);
  };

  const { locale } = useRouter();

  return (
    <Container
      className="container-fluid"
      locale={locale}
      webview={webview === 'partners'}
    >
      {breadcrumbs}

      <div className="new-launch-cover">
        <CoverSection
          marketingVideoPath={undefined}
          videoUrl={launch?.video?.url}
          marketingImageSquarePath={launch?.cover_image_path}
          marketingImageWidePath={launch?.cover_image_path}
        />
        <div className="launch-logo">
          <span>{t('new')}</span>
          <span className="launch-text">{t('launch_cover')}</span>
        </div>
      </div>

      <StickyDetails
        entity={launch}
        entityType={EntityTypes.COMPOUND}
        contactEventExtraParams={{
          entity_type: EntityTypes.LAUNCH,
          lnch_id: launch.id,
          lnch_name: launch.name,
          org_id: launch.nawy_organization_id ?? 1,
          min_price: launch.min_price,
          max_price: launch.max_price,
          dev_price: launch.primary_min_price,
          area_id: launch?.area_id,
          area_name: launch?.area?.name,
          dev_id: launch?.developer?.id,
          dev_name: launch?.developer?.name
        }}
      />

      <div className="entity-layout">
        <div className="details-side">
          <EntityDetails
            entityType={EntityTypes.COMPOUND}
            masterPlanImage={launch.map_path}
            mapData={{
              lat: launch.lat,
              long: launch.long,
              id: launch?.id,
              image: launch?.cover_image_path,
              isFiltered: true,
              name: launch.name,
              geometry: {
                type: getPolygonType(launch?.polygon_points),
                coordinates: launch?.polygon_points
              },
              parent_id: launch?.parent_compound_id
            }}
            galleryImages={launch?.compound_images}
          />

          {hasAvailability && (
            <ExploreProperties
              entityDetails={{
                type: SearchFiltersTypes.COMPOUNDS,
                id: launch.id,
                name: launch.name,
                resale_min_price: launch.min_price
              }}
              filterOptions={filterOptions}
              exploreLocation="ExplorePropertiesSection"
            />
          )}
          {launch.amenities?.length > 0 && (
            <Amenities amenities={launch.amenities} />
          )}

          {!hasAvailability && (
            <SoldOutSection
              recommendedCompounds={recommendedCompounds}
              compound={{
                id: launch.id,
                name: launch.name,
                nawy_organization_id: launch.nawy_organization_id,
                area: { id: launch.area_id, ...launch.area }
              }}
            />
          )}
          {launch.description && (
            <Description
              headtxt={launch.name}
              description={
                <div
                  dangerouslySetInnerHTML={{
                    __html: launch.description
                  }}
                />
              }
            />
          )}
          {faqs?.length > 0 && (
            <FAQs
              faqs={faqs}
              id={launch.id}
              name={launch.name}
              pageLocation="CompoundPage"
            />
          )}
        </div>

        {hasAvailability && (
          <div className="banners-side">
            <div className="form-container">
              <div className="form">
                <ContactFormContainer
                  renderedInputs={{
                    name: true,
                    location: true,
                    message: true,
                    mobileNumber: true
                  }}
                  layout={{ header: <FormHeader hasIcon={true} /> }}
                  organizationId={launch.nawy_organization_id}
                  compound={{
                    id: launch.id,
                    name: launch.name,
                    nawy_organization_id: launch?.nawy_organization_id,
                    area: { id: launch.area_id, ...launch.area },
                    developer: {
                      id: launch.developer?.id,
                      name: launch.developer?.name
                    },
                    min_price: launch.min_price,
                    max_price: launch.max_price,
                    dev_price: launch.primary_min_price
                  }}
                />
              </div>
              <RequestMeetingBtn clickHandler={handleRequestMeetingClick} />
            </div>
          </div>
        )}
      </div>
      {hasAvailability && (
        <SuggestedContainer suggestedCompounds={recommendedCompounds} />
      )}

      <QuickView isOpen={meetingForm} setIsOpen={setMeetingForm}>
        <RequestMeetingContainer
          location={{ compoundId: launch.id.toString() }}
          setFormOpen={setMeetingForm}
        />
      </QuickView>
    </Container>
  );
};

const Container = styled.div<ContainerProps>`
  padding: ${props => props.webview && '16px 0'};
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;

  .launch-logo {
    position: relative;
    border-radius: 0px 0px 12px 12px;
    background: #002741;
    height: 80px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: ${props => (props.locale === 'en' ? 'row' : 'row-reverse')};
    gap: 8px;
    color: #fff;
    text-shadow:
      0px 0px 4px rgba(0, 0, 0, 0.04),
      0px 4px 8px rgba(0, 0, 0, 0.06);
    font-family: materialMedium;
    font-size: 30px;
    text-transform: uppercase;

    .launch-text {
      color: #e0c294;
      font-family: materialSemiBold;
      font-size: 40px;
      text-transform: uppercase;
      margin-bottom: 6px;
    }
  }

  .entity-layout {
    justify-content: space-between;
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 64px;
  }

  .details-side {
    width: calc(100% - 320px);
    flex: 1;
    &.full-width {
      width: 100%;
    }
    > div:not(#breadcrumbs) {
      margin-top: 32px;
    }
  }

  .banners-side {
    width: 293px;
    height: auto;
    display: flex;
    flex-direction: column;
    margin-top: 8px;

    .form-container {
      width: 100%;
      position: -webkit-sticky;
      position: sticky;
      top: 160px;
      padding-top: 50px;

      .form {
        width: 100%;
      }
    }
  }
`;

export default LaunchView;
